<script setup lang="ts">
import { BAlert, BButton, BForm, BFormGroup, BFormInput, BModal, BFormTextarea, BFormSelect } from 'bootstrap-vue-next';
import { ref } from 'vue';

import type { ContentSubmission } from '~/api/contents';
import { createContent } from '~/api/contents';
import LoadingButton from '~/common/components/LoadingButton.vue';
import { useVueData } from '~/common/useVueData';

const { approvedCommunityMember: isApprovedCommunityMember } = useVueData();

const contentTypeOptions = [
  { value: '', text: 'Select a content type' },
  { value: 'blog', text: 'Blog' },
  { value: 'video', text: 'Video' },
  { value: 'social', text: 'Social' },
  { value: 'other', text: 'Other' },
];

const blankForm: ContentSubmission = {
  name: '',
  link: '',
  note: '',
  publication_date: '',
  content_type: '',
};
const form = ref<ContentSubmission>({ ...blankForm });
const loading = ref(false);
const error = ref<string | null>(null);
const model = defineModel<boolean>();
const emit = defineEmits<{ contentAdded: [content: ContentSubmission] }>();

const addContent = async (event: Event) => {
  event.preventDefault();
  error.value = null;
  loading.value = true;

  try {
    await createContent(form.value);

    emit('contentAdded', form.value);
    model.value = false;
  } catch {
    error.value = 'Failed to add content. Please try again.';
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  form.value = { ...blankForm };
  error.value = null;
};
</script>

<template>
  <BModal
    v-model="model"
    title="Add content"
    no-footer
    @hidden="resetForm"
  >
    <BAlert
      :model-value="!!error"
      variant="danger"
    >
      {{ error }}
    </BAlert>

    <BForm
      v-if="isApprovedCommunityMember"
      @submit="addContent"
    >
      <p class="text-muted small">
        Submit each piece of content once, even if you re-share it in multiple places.
        Submissions are reviewed by the GitLab team before points are awarded.
      </p>
      <!-- Required Fields First -->
      <BFormGroup
        label="Name"
        class="mb-3"
      >
        <BFormInput
          v-model="form.name"
          required
          aria-required="true"
          data-test-id="content-modal-input-name"
          placeholder="Title of your blog post, video, etc."
        />
      </BFormGroup>
      <BFormGroup
        label="Link"
        class="mb-3"
      >
        <BFormInput
          v-model="form.link"
          required
          aria-required="true"
          data-test-id="content-modal-input-link"
          placeholder="URL to your content"
        />
      </BFormGroup>

      <BFormGroup
        label="Content type"
        class="mb-3"
      >
        <BFormSelect
          v-model="form.content_type"
          :options="contentTypeOptions"
          required
          aria-required="true"
          data-test-id="content-modal-input-content-type"
        />
      </BFormGroup>

      <BFormGroup
        label="Publication date"
        class="mb-3"
      >
        <BFormInput
          v-model="form.publication_date"
          type="date"
          aria-required="false"
          data-test-id="content-modal-input-publication-date"
          placeholder="Publication date"
        />
      </BFormGroup>

      <!-- Optional Fields Last -->
      <BFormGroup
        label="Note (optional)"
        class="mb-3"
      >
        <BFormTextarea
          v-model="form.note"
          type="text"
          aria-required="false"
          data-test-id="content-modal-input-note"
          placeholder="Additional information about your content"
        />
      </BFormGroup>

      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
        loading-text="Adding..."
      >
        Add content
      </LoadingButton>
    </BForm>
    <template v-else>
      <p>To add content you must be an onboarded community member.</p>
      <BButton
        variant="primary"
        :to="{
          name: 'home',
          hash: '#start',
          query: { referrer: 'Submit content' } }
        "
      >
        Start onboarding
      </BButton>
    </template>
  </BModal>
</template>
